import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldAlert, Phone, Mail, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { verificationApi, VerificationStatus } from '../services/api';
import { useSarLabAuth } from '../contexts/SarLabAuthContext';
import VerificationModal from './VerificationModal';

const VerificationBanner: React.FC = () => {
  const { t } = useTranslation();
  const { isLoggedIn, currentPlayer } = useSarLabAuth();
  const [status, setStatus] = useState<VerificationStatus | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [modalType, setModalType] = useState<'sms' | 'email' | null>(null);

  useEffect(() => {
    if (isLoggedIn) {
      loadStatus();
    }
  }, [isLoggedIn]);

  const loadStatus = async () => {
    try {
      const result = await verificationApi.getStatus();
      if (result.success && result.data) {
        setStatus(result.data.data);
      }
    } catch {
      // Ignore errors
    }
  };

  const handleSuccess = () => {
    loadStatus();
    setModalType(null);
  };

  // Don't show if not logged in, dismissed, or fully verified
  if (!isLoggedIn || dismissed || !status || status.isFullyVerified) {
    return null;
  }

  // Check what needs verification (only show phone if player has a phone number)
  const needsPhone = !status.phoneVerified && currentPlayer?.phone;
  const needsEmail = !status.emailVerified;

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-b border-amber-500/30"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                  <ShieldAlert className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-white font-medium">
                    {t('verification:bannerTitle', 'Hesabinizi dogrulayin')}
                  </p>
                  <p className="text-gray-400 text-sm">
                    {t('verification:bannerDesc', 'Tum ozelliklere erisim icin dogrulama yapın')}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {needsPhone && (
                  <button
                    onClick={() => setModalType('sms')}
                    className="flex items-center gap-2 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    <span className="hidden sm:inline">
                      {t('verification:verifyPhone', 'Telefon Dogrula')}
                    </span>
                  </button>
                )}

                {needsEmail && (
                  <button
                    onClick={() => setModalType('email')}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    <span className="hidden sm:inline">
                      {t('verification:verifyEmail', 'Email Dogrula')}
                    </span>
                  </button>
                )}

                <button
                  onClick={() => setDismissed(true)}
                  className="p-2 hover:bg-dark-700 rounded-lg text-gray-400 hover:text-white transition-colors"
                  title={t('common:dismiss', 'Kapat')}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Verification Modal */}
      {modalType && (
        <VerificationModal
          isOpen={!!modalType}
          onClose={() => setModalType(null)}
          type={modalType}
          onSuccess={handleSuccess}
        />
      )}
    </>
  );
};

export default VerificationBanner;
